import { Component, EventEmitter, forwardRef, Input, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Emplacement } from '../entites/emplacement';
import { EmployeEntite } from '../entites/employe';
import { DonneesBaseService } from '../services/donnees-base-service';

/**
 * Option for the assignation filter - can be either an employee or an emplacement.
 */
export interface AssignationOption {
  label: string;
  value: string;
  type: 'employe' | 'emplacement';
  id?: number;
}

/**
 * Reusable component for filtering by "Assigné à" (employee and/or emplacement).
 * Features two separate multiselects:
 * - One for emplacements (locations)
 * - One for employés (employees)
 * 
 * The filter logic includes the employee's current emplacement, so filtering by an emplacement
 * will also include items assigned to employees currently at that emplacement.
 * 
 * @example
 * ```html
 * <!-- As a column filter in p-table -->
 * <p-columnFilter field="assignation" matchMode="assignationContains" display="menu"
 *     [showMatchModes]="false" [showOperator]="false" [showAddButton]="false">
 *     <ng-template pTemplate="filter" let-value let-filter="filterCallback">
 *         <app-assignation-filter
 *             [(ngModel)]="selectionAssignations"
 *             (onChange)="filter($event)">
 *         </app-assignation-filter>
 *     </ng-template>
 * </p-columnFilter>
 * ```
 */
@Component({
  selector: 'app-assignation-filter',
  template: `
    <div class="flex flex-column gap-3">
      <!-- Emplacements multiselect -->
      <div class="flex flex-column gap-1">
        <label class="text-500 text-sm font-medium">
          <i class="pi pi-map-marker mr-1"></i>Emplacements
        </label>
      <p-multiSelect 
        [options]="emplacementOptions" 
        [(ngModel)]="selectedEmplacements"
        placeholder="Filtrer par emplacement..."
        [filter]="true"
        filterPlaceholder="Rechercher..."
        [showToggleAll]="true"
        [showClear]="true"
        optionLabel="label"
        optionValue="value"
        (onChange)="onSelectionChange()"
        (onClear)="onClearEmplacements()"
        [style]="{'min-width': '15rem'}">
        <ng-template let-option pTemplate="item">
          <div class="flex align-items-center gap-2">
            <i class="pi pi-map-marker text-500"></i>
            <span>{{option.label}}</span>
          </div>
        </ng-template>
      </p-multiSelect>
      </div>

      <!-- Employés multiselect -->
      <div class="flex flex-column gap-1">
        <label class="text-500 text-sm font-medium">
          <i class="pi pi-user mr-1"></i>Employés
        </label>
      <p-multiSelect 
        [options]="employeOptions" 
        [(ngModel)]="selectedEmployes"
        placeholder="Filtrer par employé..."
        [filter]="true"
        filterPlaceholder="Rechercher..."
        [showToggleAll]="true"
        [showClear]="true"
        optionLabel="label"
        optionValue="value"
        (onChange)="onSelectionChange()"
        (onClear)="onClearEmployes()"
        [style]="{'min-width': '15rem'}">
        <ng-template let-option pTemplate="item">
          <div class="flex align-items-center gap-2">
            <i class="pi pi-user text-500"></i>
            <span>{{option.label}}</span>
          </div>
        </ng-template>
      </p-multiSelect>
      </div>

      <!-- Selection summary -->
      <div *ngIf="hasSelection" class="text-xs text-500 flex align-items-center gap-1">
        <i class="pi pi-info-circle"></i>
        <span>{{getSelectionSummary()}}</span>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
    }
  `],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AssignationFilterComponent),
      multi: true
    }
  ]
})
export class AssignationFilterComponent implements OnInit, ControlValueAccessor {
  /**
   * Emits when the selection changes. The value is an array of strings
   * that can be used with the assignationContains filter.
   */
  @Output() onChange = new EventEmitter<string[]>();

  /** Options for the emplacements multiselect */
  emplacementOptions: AssignationOption[] = [];

  /** Options for the employés multiselect */
  employeOptions: AssignationOption[] = [];

  /** Currently selected emplacement values */
  selectedEmplacements: string[] = [];

  /** Currently selected employé values */
  selectedEmployes: string[] = [];

  private onTouched: () => void = () => {};
  private onChangeFn: (value: string[]) => void = () => {};

  constructor(private sdb: DonneesBaseService) {}

  ngOnInit(): void {
    this.initializeOptions();
  }

  /**
   * Initializes the options from the base data service.
   */
  private initializeOptions(): void {
    // Build emplacement options
    const emplacements = this.sdb.Emplacements() || [];
    this.emplacementOptions = emplacements.map(e => ({
      label: e.nom,
      value: e.nom,
      type: 'emplacement' as const,
      id: e.emplacementId
    }));

    // Build employé options
    const employes = this.sdb.Employes() || [];
    this.employeOptions = employes.map(e => ({
      label: `${e.prenom} ${e.nom}`,
      value: `${e.prenom} ${e.nom}`,
      type: 'employe' as const,
      id: e.employeId
    }));
  }

  /**
   * Returns true if any selection is made.
   */
  get hasSelection(): boolean {
    return this.selectedEmplacements?.length > 0 || this.selectedEmployes?.length > 0;
  }

  /**
   * Returns a summary of the current selection.
   */
  getSelectionSummary(): string {
    const parts: string[] = [];
    if (this.selectedEmplacements.length > 0) {
      parts.push(`${this.selectedEmplacements.length} emplacement(s)`);
    }
    if (this.selectedEmployes.length > 0) {
      parts.push(`${this.selectedEmployes.length} employé(s)`);
    }
    return parts.join(' et ');
  }

  /**
   * Called when any selection changes. Combines both selections
   * and emits the filter value.
   */
  onSelectionChange(): void {
    const combinedValue = this.getCombinedFilterValue();
    this.onChangeFn(combinedValue);
    this.onChange.emit(combinedValue);
    this.onTouched();
  }

  /**
   * Called when the emplacements multiselect is cleared.
   */
  onClearEmplacements(): void {
    this.selectedEmplacements = [];
    this.onSelectionChange();
  }

  /**
   * Called when the employés multiselect is cleared.
   */
  onClearEmployes(): void {
    this.selectedEmployes = [];
    this.onSelectionChange();
  }

  /**
   * Returns the combined filter value from both multiselects.
   * Returns null if no selection is made (to clear the filter).
   */
  private getCombinedFilterValue(): string[] {
    const combined = [...this.selectedEmplacements, ...this.selectedEmployes];
    return combined.length > 0 ? combined : null;
  }

  // ControlValueAccessor implementation

  writeValue(value: string[]): void {
    if (value && Array.isArray(value)) {
      // Distribute the values between emplacements and employés
      this.selectedEmplacements = value.filter(v => 
        this.emplacementOptions.some(opt => opt.value === v)
      );
      this.selectedEmployes = value.filter(v => 
        this.employeOptions.some(opt => opt.value === v)
      );
    } else {
      this.selectedEmplacements = [];
      this.selectedEmployes = [];
    }
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // Could be implemented if needed
  }

  /**
   * Clears all selections.
   */
  clearSelection(): void {
    this.selectedEmplacements = [];
    this.selectedEmployes = [];
    this.onSelectionChange();
  }
}

